// ============================================================
//  src/admin/pages/AdminBroadcastsPage.js
//  Platform-wide, read-only view of every broadcast campaign
//  across every bot/owner — for monitoring stuck sends, abuse,
//  and delivery health at a glance.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { listBroadcasts } from '../api/adminClient';
import { PageHeader, Badge, Pagination } from '../components/UI';
import DataTable from '../components/DataTable';

const STATUS_TONE = {
  draft: 'muted', scheduled: 'warning', sending: 'primary', active: 'primary',
  recurring: 'primary', paused: 'warning', stopped: 'danger', sent: 'success', failed: 'danger',
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d.replace(' ', 'T'));
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function AdminBroadcastsPage() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 30;

  const load = useCallback(() => {
    setLoading(true);
    listBroadcasts({ page, limit, status, search })
      .then(res => { setBroadcasts(res.data.broadcasts); setTotal(res.data.total); })
      .catch(err => toast.error(err?.response?.data?.message || 'Could not load broadcasts'))
      .finally(() => setLoading(false));
  }, [page, status, search]);

  useEffect(() => { load(); }, [load]);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const columns = [
    {
      key: 'campaign', label: 'Campaign', primary: true,
      render: b => (
        <div>
          <div className="bf-cell-strong">{b.bName}</div>
          <div className="bf-cell-muted">{b.botName}</div>
        </div>
      ),
    },
    {
      key: 'owner', label: 'Owner',
      render: b => (
        <div>
          <div>{b.sFname} {b.sLname}</div>
          <div className="bf-cell-muted">{b.sEmail}</div>
        </div>
      ),
    },
    { key: 'type', label: 'Type', render: b => b.bSendType },
    {
      key: 'progress', label: 'Progress',
      render: b => (
        <span>
          {b.bSentCount || 0}/{b.bTotalCount || 0} sent
          {b.bFailedCount > 0 && <span style={{ color: 'var(--bf-danger)' }}> · {b.bFailedCount} failed</span>}
        </span>
      ),
    },
    { key: 'status', label: 'Status', render: b => <Badge tone={STATUS_TONE[b.bStatus] || 'muted'}>{b.bStatus}</Badge> },
    { key: 'created', label: 'Created', render: b => fmtDate(b.bCreatedAt) },
  ];

  return (
    <div className="bf-page">
      <PageHeader title="Broadcasts" subtitle={`${total.toLocaleString('en-NG')} campaigns across all bots — monitor delivery health and stuck sends`} />

      <div className="bf-toolbar">
        <input
          className="bf-input"
          placeholder="Search by campaign, bot, or owner email…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select className="bf-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="sending">Sending</option>
          <option value="active">Active</option>
          <option value="recurring">Recurring</option>
          <option value="paused">Paused</option>
          <option value="sent">Sent</option>
          <option value="stopped">Stopped</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="bf-card">
        <div className="bf-card-body-flush">
          <DataTable
            columns={columns}
            rows={broadcasts}
            keyField="broadcastId"
            loading={loading}
            loadingMessage="Loading broadcasts…"
            emptyMessage="No broadcast campaigns found."
          />
        </div>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} limit={limit} />
    </div>
  );
}